import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AutomataDevelopmentService,
  Automation,
  ChoicesInterface,
  Context,
  OutputParameter,
  TaskDefinition,
} from '@vendasta/automata';
import { WorkflowStep } from '@vendasta/automata/lib/_internal/objects/automation';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, take, takeUntil, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../automation/automation-view.service';
import { TaskDefinitionsService } from '../../../data-services/task-definitions.service';
import { AutomationsRouterService } from '../../../shared/automations-router.service';
import { TriggerEditorControlComponent } from '../../components/common/controls/trigger-editor-control/trigger-editor-control.component';
import { FilterPanelComponent } from '../../components/filters';
import { AutomationErrorHandlerService, ValidationService } from '../../services';
import { AutomationsSideDrawerService } from '../../side-drawer/automations-side-drawer-service';
import {
  AutomataClosingSideDrawerComponent,
  AutomataSideDrawerContentDirective,
  AutomataSideDrawerFooterDirective,
} from '../../side-drawer/closing-side-drawer/automata-closing-side-drawer.component';
import { TriggerService } from '../../trigger.service';
import { CustomOutputParametersComponent } from '../custom-output-parameters/custom-output-parameters.component';
import { CustomOutputParamsService } from '../custom-output-parameters/custom-output-params.service';
import { WORKFLOW_TRIGGERS } from '../trigger-definitions';
import { AppRequirementsAlertComponent } from '../../../shared/app-requirements-alert/app-requirements-alert.component';
import { AutomataContextService } from '@galaxy/automata/shared';

@Component({
  selector: 'automata-trigger-panel',
  templateUrl: './dynamic-trigger-panel-loader.component.html',
  styleUrls: ['./dynamic-trigger-panel-loader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    GalaxyAlertModule,
    MatProgressSpinnerModule,
    FilterPanelComponent,
    TriggerEditorControlComponent,
    AppRequirementsAlertComponent,
    CustomOutputParametersComponent,
    AutomataClosingSideDrawerComponent,
    AutomataSideDrawerContentDirective,
    AutomataSideDrawerFooterDirective,
    GalaxyFormFieldModule,
    MatInputModule,
    TextFieldModule,
    ReactiveFormsModule,
    MatDividerModule,
    FormsModule,
  ],
})
export class DynamicTriggerPanelLoaderComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() preventClose: boolean;
  @Input() automation: Automation;
  @Input() taskDefinitionId: string;
  @Input() triggerChoices: ChoicesInterface;
  triggerFilters: WorkflowStep[];

  @ViewChild('triggerEditorControl', { static: false })
  triggerEditorControl: TriggerEditorControlComponent;

  @ViewChild('filterPanel', { static: false })
  filterPanel: FilterPanelComponent;

  isProcessing = false;

  /** When true, some component within the panel is loading and not ready for use. */
  isLoading = true;

  isEditable$ = of(false);

  isAvailableOnSubscriptionLevel$: Observable<boolean>;

  name = '';
  upgradeTitle = 'AUTOMATIONS.EDITOR.DEFAULT_UPGRADE_SUBSCRIPTION_TITLE';
  upgradeDescription = 'AUTOMATIONS.EDITOR.DEFAULT_UPGRADE_SUBSCRIPTION_DESCRIPTION';

  supportsCustomOutputParameters = false;
  @ViewChild(CustomOutputParametersComponent, { static: false })
  public customOutputParameters: CustomOutputParametersComponent;

  private destroyed$$ = new Subject<void>();
  stepNotesControl: FormControl<string> = new FormControl<string>('');

  taskDefinition$: Observable<TaskDefinition>;
  context = Context;
  protected automationContext = this.automationContextService.currentContext;

  constructor(
    private readonly automationService: AutomationViewService,
    private readonly translateService: TranslateService,
    private readonly triggerService: TriggerService,
    private readonly sideMenuService: AutomationsSideDrawerService,
    private readonly snackbarService: SnackbarService,
    private readonly errorHandler: AutomationErrorHandlerService,
    private readonly validationService: ValidationService,
    private readonly cdr: ChangeDetectorRef,
    public sanitizer: DomSanitizer,
    private readonly taskDefinitionsService: TaskDefinitionsService,
    private router: AutomationsRouterService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly customOutputParamsService: CustomOutputParamsService,
    private readonly automataDevelopmentService: AutomataDevelopmentService,
    private readonly automationContextService: AutomataContextService,
  ) {}

  ngOnInit(): void {
    this.taskDefinitionsService.setCurrentTaskDefinitionId(this.taskDefinitionId, true);
    this.isEditable$ = this.automationService.isEditable$(this.automation);
    this.triggerFilters = this.automation?.triggerFilters?.map((x) => ({ ...x }) as WorkflowStep) || [];
    this.setTaskDefinitionDetails(this.taskDefinitionId);
    this.stepNotesControl.setValue(this.automation?.triggerStep?.notes || '');
    this.taskDefinition$ = this.automataDevelopmentService.getTaskDefinition(this.taskDefinitionId);
  }

  selectedNewTrigger(taskDefinitionId: string): void {
    this.taskDefinitionsService.setCurrentTaskDefinitionId(taskDefinitionId, true);
    this.filterPanel?.removeAll();
    this.setTaskDefinitionDetails(taskDefinitionId);
  }

  setTaskDefinitionDetails(taskDefinitionId: string): void {
    this.isAvailableOnSubscriptionLevel$ = this.taskDefinitionsService
      .checkTaskDefinitionAvailableAtSubscriptionLevel(taskDefinitionId)
      .pipe(take(1), shareReplay(1));
    const triggerDefinition = WORKFLOW_TRIGGERS[taskDefinitionId];
    this.name = triggerDefinition?.name;
    this.upgradeTitle =
      triggerDefinition?.upgradeSubscriptionTitle || 'AUTOMATIONS.EDITOR.DEFAULT_UPGRADE_SUBSCRIPTION_TITLE';
    this.upgradeDescription =
      triggerDefinition?.upgradeSubscriptionDescription ||
      'AUTOMATIONS.EDITOR.DEFAULT_UPGRADE_SUBSCRIPTION_DESCRIPTION';
    this.supportsCustomOutputParameters = triggerDefinition?.supportsCustomOutputParameters;
    if (!this.supportsCustomOutputParameters && this.automation?.triggerStep?.customOutputParameters?.length > 0) {
      // Clear the custom output parameters if the selected step doesn't support them.
      this.automation.triggerStep.customOutputParameters = [];
    }
    this.cdr.detectChanges();
  }

  subscribeToLoadingEvents(
    triggerEditorControl: TriggerEditorControlComponent,
    filterPanel: FilterPanelComponent,
  ): void {
    // Ensure all controls are ready.
    this.triggerEditorControl = this.triggerEditorControl || triggerEditorControl;
    this.filterPanel = this.filterPanel || filterPanel;
    if (!this.triggerEditorControl || !this.filterPanel) {
      return;
    }

    // If the trigger editor is ready to save, this is ready to save
    combineLatest([this.triggerEditorControl.savingEnabled$.pipe(map((val) => !val)), this.filterPanel.isLoading$])
      .pipe(takeUntil(this.destroyed$$))
      .subscribe((savingDisabled) => {
        // If any subview cannot yet be saved, then this panel is still loading.
        this.isLoading = savingDisabled.includes(true);

        // This is required to get the save button to appropriately toggle.
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.unsubscribe();
  }

  save(): void {
    // The trigger editor may have updated its automation, so make sure to use that one.
    this.automation = this.triggerEditorControl.automation;

    let taskDefinitionId =
      this.automation.triggerStep?.taskDefinitionId ||
      this.triggerEditorControl.componentRef?.instance?.taskDefinitionId;
    let choices: ChoicesInterface = this.automation.triggerStep?.choices;
    let data = '';
    // If the trigger choices editor is open, we need to validate and save those rules
    if (this.triggerEditorControl.isEditing) {
      this.triggerEditorControl.componentRef.instance
        .getControlsToValidateOnSave()
        .forEach((control) => this.validationService.updateControlValidity(control));
      if (!this.triggerEditorControl.componentRef.instance.isFormValid()) {
        this.snackbarService.errorSnack(this.translateService.instant('AUTOMATIONS.COMMON.FORM_INVALID'));
        return;
      }

      let rules = this.triggerEditorControl.triggerChoices.rules;
      if (!rules || rules.length === 0) {
        rules = this.triggerEditorControl.componentRef.instance.getBaseRules();
      }
      rules = this.triggerEditorControl.componentRef.instance.fillRuleValues(rules);

      this.triggerEditorControl.triggerChoices.rules = rules;
      choices = this.triggerEditorControl.triggerChoices;
      taskDefinitionId = this.triggerEditorControl.componentRef.instance.taskDefinitionId;
      data = JSON.stringify(this.triggerEditorControl.componentRef.instance.getData());
    }

    let triggerFilters: WorkflowStep[] = [];
    if (this.filterPanel) {
      this.filterPanel
        .getControlsToValidateOnSave()
        .forEach((control) => this.validationService.updateControlValidity(control));
      if (!this.filterPanel.isFormValid()) {
        this.snackbarService.errorSnack(this.translateService.instant('AUTOMATIONS.COMMON.FORM_INVALID'));
        return;
      }
      triggerFilters = this.filterPanel.getWorkflowSteps();
    }
    let customOutputParameters: OutputParameter[] = [];
    if (this.supportsCustomOutputParameters && this.customOutputParameters) {
      customOutputParameters = this.customOutputParamsService.buildCustomOutputParametersFromReturnValues();
    }

    this.isProcessing = true;

    this.triggerService
      .saveTriggerStep(
        choices,
        triggerFilters,
        taskDefinitionId,
        data,
        customOutputParameters,
        this.stepNotesControl.value,
      )
      .pipe(
        take(1),
        catchError((err) => {
          this.errorHandler.displayError(err, true);
          if (err.error?.message) {
            const validationErr = JSON.parse(err.error.message);
            if (validationErr?.details?.stepId) {
              this.errorHandler.deleteValidationError(validationErr.details.stepId);
              this.errorHandler.setValidationError(validationErr.details);
            }
          }
          this.isProcessing = false;
          this.cdr.detectChanges();
          return of(err);
        }),
        tap(() => this.sideMenuService.close()),
      )
      .subscribe();
  }

  editingPreventedWarning(): Observable<string> {
    return this.automationService.getEditingPreventedWarning(this.automation);
  }

  upgrade(): void {
    this.analyticsService.trackEvent('upgrade-' + this.taskDefinitionId, 'automations', 'click');
    this.router.navigateByUrl('/upgrade-subscription-tier?showUpgradeDialog=true');
  }
}
